import { useMemo } from 'react';
import { useInstitutionChange } from '@/hooks/useInstitutionChange';
import { useInstitutionsStore } from '@/stores/institutions';
import { useUsersStore } from '@/stores/users';

/**
 * Institutions the current user is allowed to switch to, filtered by
 * availableInstitutionIds unless the user is a super admin (sees all),
 * sorted alphabetically by name.
 * @returns The current institution, the available institution options,
 * whether the user is a super admin, and the institution change handler.
 */
export const useInstitutionOptions = () => {
  const { currentInstitution, institutions } = useInstitutionsStore(
    (state) => ({
      currentInstitution: state.currentInstitution,
      institutions: state.institutions,
    }),
  );
  const availableInstitutionIds = useUsersStore(
    (state) => state.availableInstitutionIds,
  );
  const isSuperAdmin = useUsersStore((state) => state.checkIsSuperAdmin());
  const onInstitutionChange = useInstitutionChange();

  const institutionOptions = useMemo(() => {
    const availableInstitutions = isSuperAdmin
      ? institutions
      : institutions.filter((item) =>
          availableInstitutionIds.includes(item.id),
        );

    return availableInstitutions
      .map((item) => ({ ...item, title: item.name }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [institutions, availableInstitutionIds, isSuperAdmin]);

  return {
    currentInstitution,
    institutionOptions,
    isSuperAdmin,
    onInstitutionChange,
  };
};
